/**
 * Couch generator — a sofa / couch built from box geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = width (along the sofa length; sitter faces +Y)
 *   - Y = depth (front–back)
 *   - Z = height
 *
 * Local anchor: origin at the geometric centre of the footprint at Z = 0.
 *
 * The couch is a compound group so seat, back, armrests, and frame can each
 * carry independent materials (fabric cushions, wooden frame, metal legs).
 *
 * Width presets correspond to standard sofa seat counts:
 *   loveseat — 1.40 m   (2-seat compact)
 *   sofa     — 1.90 m   (2.5-seat standard)
 *   sofa_3   — 2.20 m   (3-seat standard)
 *   custom   — user-defined
 *
 * Armrest styles:
 *   square  — a rectangular flat-top arm running the full seat depth.
 *   sloped  — the arm panel tapers down at the front (track-arm silhouette),
 *             approximated by two stacked boxes of decreasing height.
 *   none    — no armrests (bench-style sofa)
 *
 * Leg styles:
 *   block   — four short rectangular block feet under the frame corners.
 *   hairpin — hairpin-style approximation: two flat square posts per corner
 *             angled slightly outward (approximated as thin diagonal boxes).
 *   none    — flush base (platform sofa — frame sits directly on the floor)
 *
 * Seat cushion count (1–4) divides the seat width into equal visible segments,
 * implemented as a series of thin vertical division lines (box seams).
 */

import type { PrimitiveGeometry } from "../primitives.ts";
import {
  buildBox,
  concatBoxes,
  concatPrimitiveGeometries,
  transformGeometry,
} from "./generatorGeom.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorChild,
  GeneratorLocalTransform,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  defineGenerator,
  enumParam,
  intParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface CouchParams extends GeneratorParams {
  widthPreset: string;
  totalWidth: number;
  seatDepth: number;
  seatHeight: number;
  seatThickness: number;
  backHeight: number;
  backAngle: number;
  backThickness: number;
  armStyle: string;
  armWidth: number;
  armHeight: number;
  cushionCount: number;
  legStyle: string;
  legHeight: number;
}

const COUCH_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "couch-size",
    label: "Size",
    params: [
      {
        id: "widthPreset",
        label: "Preset",
        type: "enum",
        default: "sofa",
        options: [
          { value: "loveseat", label: "Loveseat (1.40 m)" },
          { value: "sofa", label: "Sofa (1.90 m)" },
          { value: "sofa_3", label: "3-Seat (2.20 m)" },
          { value: "custom", label: "Custom" },
        ],
      },
      {
        id: "totalWidth",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 1.9,
        min: 0.8,
        max: 4.0,
        step: 0.05,
        showIf: { id: "widthPreset", value: "custom" },
      },
      {
        id: "seatDepth",
        label: "Depth",
        type: "number",
        unit: "meters",
        default: 0.88,
        min: 0.6,
        max: 1.4,
        step: 0.025,
      },
      {
        id: "seatHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.44,
        min: 0.3,
        max: 0.65,
        step: 0.01,
      },
    ],
  },
  {
    type: "section",
    id: "couch-back",
    label: "Back",
    params: [
      {
        id: "backHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.72,
        min: 0.3,
        max: 1.2,
        step: 0.025,
      },
      {
        id: "backAngle",
        label: "Angle",
        type: "number",
        unit: "degrees",
        default: 12,
        min: 0,
        max: 30,
        step: 1,
      },
      {
        id: "backThickness",
        label: "Thickness",
        type: "number",
        unit: "meters",
        default: 0.16,
        min: 0.08,
        max: 0.3,
        step: 0.01,
      },
    ],
  },
  {
    type: "section",
    id: "couch-arms",
    label: "Armrests",
    params: [
      {
        id: "armStyle",
        label: "Style",
        type: "enum",
        default: "square",
        options: [
          { value: "square", label: "Square" },
          { value: "sloped", label: "Track Arm" },
          { value: "none", label: "None" },
        ],
      },
      {
        id: "armWidth",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 0.22,
        min: 0.1,
        max: 0.45,
        step: 0.01,
        showIf: { id: "armStyle", value: ["square", "sloped"] },
      },
      {
        id: "armHeight",
        label: "Arm Height",
        type: "number",
        unit: "meters",
        default: 0.22,
        min: 0.05,
        max: 0.5,
        step: 0.01,
        description: "Height of arm surface above the seat.",
        showIf: { id: "armStyle", value: ["square", "sloped"] },
      },
      {
        id: "cushionCount",
        label: "Seat Cushions",
        type: "number",
        unit: "count",
        default: 3,
        min: 1,
        max: 5,
        step: 1,
      },
    ],
  },
  {
    type: "section",
    id: "couch-legs",
    label: "Legs",
    defaultCollapsed: true,
    params: [
      {
        id: "legStyle",
        label: "Style",
        type: "enum",
        default: "block",
        options: [
          { value: "block", label: "Block" },
          { value: "none", label: "None (Platform)" },
        ],
      },
      {
        id: "legHeight",
        label: "Height",
        type: "number",
        unit: "meters",
        default: 0.1,
        min: 0.0,
        max: 0.3,
        step: 0.01,
        showIf: { id: "legStyle", value: "block" },
      },
      {
        id: "seatThickness",
        label: "Seat Thickness",
        type: "number",
        unit: "meters",
        default: 0.14,
        min: 0.06,
        max: 0.25,
        step: 0.01,
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Width presets
// ---------------------------------------------------------------------------

const WIDTH_PRESETS: Record<string, number> = {
  loveseat: 1.4,
  sofa: 1.9,
  sofa_3: 2.2,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function lt(pos: [number, number, number]): GeneratorLocalTransform {
  return { position: pos, rotation: [0, 0, 0], scale: [1, 1, 1] };
}

/**
 * Build one armrest assembly in child-local space (cx = 0 at the arm centreline).
 * The arm body runs from Z = legHeight to Z = seatHeight + armHeight.
 */
function buildArmAssembly(
  seatDepth: number,
  seatHeight: number,
  legHeight: number,
  armWidth: number,
  armHeight: number,
  armStyle: string,
): PrimitiveGeometry {
  const topZ = seatHeight + armHeight;
  const bodyH = Math.max(0.02, topZ - legHeight);
  const bodyCZ = legHeight + bodyH * 0.5;

  if (armStyle === "sloped") {
    // Track arm: rear two-thirds of the body is full height; front third tapers
    // down to about 60% of the arm height, creating a clean swept silhouette.
    const rearL = seatDepth * 0.6;
    const frontL = seatDepth * 0.4;
    const frontH = bodyH * 0.55;
    const rearY = -(seatDepth * 0.5 - rearL * 0.5); // rear section Y centre
    const frontY = seatDepth * 0.5 - frontL * 0.5; // front section Y centre
    return concatBoxes([
      { cx: 0, cy: rearY, cz: bodyCZ, sx: armWidth, sy: rearL, sz: bodyH },
      {
        cx: 0,
        cy: frontY,
        cz: legHeight + frontH * 0.5,
        sx: armWidth,
        sy: frontL,
        sz: frontH,
      },
    ]);
  }

  // Square — single solid arm panel
  return concatBoxes([
    { cx: 0, cy: 0, cz: bodyCZ, sx: armWidth, sy: seatDepth, sz: bodyH },
  ]);
}

/**
 * Build the back cushion geometry in child-local space.
 * Origin = hinge at Z = 0. Extends from Z = 0 to Z = backHeight.
 */
function buildBack(
  cushionW: number,
  backHeight: number,
  backThick: number,
): PrimitiveGeometry {
  return transformGeometry(buildBox(cushionW, backThick, backHeight), [
    0,
    0,
    backHeight * 0.5,
  ]);
}

/**
 * Build the seat cushion assembly: one continuous block divided by N-1 thin
 * seam lines (narrow gap boxes in contrasting geometry grouping).
 */
function buildSeatCushions(
  cushionW: number,
  seatDepth: number,
  seatT: number,
  n: number,
): PrimitiveGeometry {
  if (n <= 1) {
    return transformGeometry(buildBox(cushionW, seatDepth, seatT), [
      0,
      0,
      seatT * 0.5,
    ]);
  }
  const segW = cushionW / n;
  const seamW = 0.008; // 8 mm seam gap
  const parts: PrimitiveGeometry[] = [];
  for (let i = 0; i < n; i++) {
    const cx = -cushionW * 0.5 + segW * (i + 0.5);
    const sw = Math.max(0.01, segW - (i < n - 1 ? seamW : 0));
    parts.push(
      transformGeometry(buildBox(sw, seatDepth, seatT), [cx, 0, seatT * 0.5]),
    );
  }
  return concatPrimitiveGeometries(parts);
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

function buildCouch(
  p: CouchParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const preset = enumParam(p, "widthPreset", "sofa");
  const totalW =
    preset === "custom"
      ? numParam(p, "totalWidth", 1.9)
      : (WIDTH_PRESETS[preset] ?? 1.9);
  const seatD = numParam(p, "seatDepth", 0.88);
  const seatH = numParam(p, "seatHeight", 0.44);
  const seatT = numParam(p, "seatThickness", 0.14);
  const backH = numParam(p, "backHeight", 0.72);
  const backAng = numParam(p, "backAngle", 12) * (Math.PI / 180);
  const backT = numParam(p, "backThickness", 0.16);
  const armSty = enumParam(p, "armStyle", "square");
  const armW = armSty === "none" ? 0 : numParam(p, "armWidth", 0.22);
  const armH = armSty === "none" ? 0 : numParam(p, "armHeight", 0.22);
  const cushCount = intParam(p, "cushionCount", 3);
  const legSty = enumParam(p, "legStyle", "block");
  const legH = legSty === "none" ? 0 : numParam(p, "legHeight", 0.1);

  const children: GeneratorChild[] = [];

  // ── Frame / base ──────────────────────────────────────────────────────────
  // A low box that provides the structural base below the seat cushion.
  // Spans the full outer width (including arm width) and the full depth.
  const outerW = totalW + armW * 2;
  const frameH = Math.max(0.04, seatH - seatT - legH);
  if (frameH > 0.01) {
    children.push({
      roleId: "frame",
      name: "Frame",
      objectKind: "cube",
      geometry: buildBox(outerW, seatD, frameH),
      localTransform: lt([0, 0, legH + frameH * 0.5]),
    });
  }

  // ── Legs ──────────────────────────────────────────────────────────────────
  if (legSty === "block" && legH > 0) {
    const legSz = 0.07;
    const legXOff = outerW * 0.5 - legSz * 0.5;
    const legYOff = seatD * 0.5 - legSz * 0.5;
    const legGeo = buildBox(legSz, legSz, legH);
    // Also add centre legs for longer sofas to avoid a floating appearance.
    const extraLegs: number[] = [];
    if (totalW > 1.5) extraLegs.push(0);
    for (const sy of [1, -1]) {
      for (const sx of [-1, 1, ...extraLegs]) {
        children.push({
          roleId: `leg_${sx}_${sy}`,
          name: "Leg",
          objectKind: "cube",
          geometry: legGeo,
          localTransform: lt([
            sx === 0 ? 0 : sx * legXOff,
            sy * legYOff,
            legH * 0.5,
          ]),
        });
      }
    }
  }

  // ── Seat cushions ─────────────────────────────────────────────────────────
  // Sit between the armrests, on top of the frame.
  const cushionW = Math.max(0.1, totalW);
  const seatGeo = buildSeatCushions(
    cushionW,
    seatD,
    seatT,
    Math.max(1, cushCount),
  );
  children.push({
    roleId: "seat",
    name: "Seat",
    objectKind: "cube",
    geometry: seatGeo,
    // seatGeo already has Z from 0 to seatT; offset to place on top of frame.
    localTransform: lt([0, 0, seatH - seatT]),
  });

  // ── Back cushion ──────────────────────────────────────────────────────────
  // Hinge at rear of seat top, matching chair/armchair convention.
  // Y: +Y face of back at Y = -(seatD/2), so child Y = -(seatD/2 + backT/2).
  const backGeo = buildBack(cushionW, backH, backT);
  children.push({
    roleId: "back",
    name: "Back",
    objectKind: "cube",
    geometry: backGeo,
    localTransform: {
      position: [0, -(seatD * 0.5 + backT * 0.5), seatH],
      rotation: [backAng, 0, 0],
      scale: [1, 1, 1],
    },
  });

  // ── Armrests ──────────────────────────────────────────────────────────────
  if (armSty !== "none") {
    const armGeo = buildArmAssembly(seatD, seatH, legH, armW, armH, armSty);
    const armXOff = totalW * 0.5 + armW * 0.5;
    children.push({
      roleId: "arm_l",
      name: "Armrest L",
      objectKind: "cube",
      geometry: armGeo,
      localTransform: lt([-armXOff, 0, 0]),
    });
    children.push({
      roleId: "arm_r",
      name: "Armrest R",
      objectKind: "cube",
      geometry: armGeo,
      localTransform: lt([armXOff, 0, 0]),
    });
  }

  return {
    kind: "group",
    parent: { name: "Couch" },
    children,
  };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const COUCH_GENERATOR: Generator = defineGenerator<CouchParams>({
  id: "couch",
  label: "Couch",
  category: "prop",
  paramSpecs: COUCH_SPECS,
  defaultParams: paramsFromSpecs(COUCH_SPECS) as CouchParams,
  build: buildCouch,
});
